<template>
  <div class="pupils">
    <FilterBar @searchChanged="setSearchValue">
      <template v-slot:filter>
        <div>
          <ClassDropdown
            :classSelected="filterValue.group"
            @classValueChanged="updateFilteredClass"
          ></ClassDropdown>
        </div>
        <div>
          <button
            type="button"
            class="filter-bar-button focus:outline-none hover:opacity-75"
            v-bind:class="
              filterHasValues ? 'filter-item-button-on' : 'bg-white'
            "
            @click="showFilter()"
          >
            <i class="fas fa-filter"></i> Filters
          </button>
        </div>
      </template>

      <template v-slot:actions> </template>
    </FilterBar>

    <PupilFilterModal
      class=""
      v-if="filterIsVisible"
      :values="filterValue"
      @apply-filter="applyFilter"
      @close="showFilter"
    >
    </PupilFilterModal>

    <p v-if="showMessage" class="save-notice ml-6 mb-6 font-bold">
      {{ msg }} <i class="far fa-check-circle"></i>
    </p>

    <table
      class="w-95p mx-auto mr-8 mb-2 h-10 pt-2 align-text-bottom bg-white rounded text-left text-gray-400"
    >
      <thead class="bg-gray-100 w-full">
        <tr>
          <th class="pl-2 w-4/12">
            Pupil Name
            <i
              class="fas fa-sort tbh-sort"
              v-bind:class="
                'name' === sortBy ? 'text-blue-500' : 'text-gray-400'
              "
            ></i>
          </th>
          <th
            v-for="(col, index) in swimOutcomes"
            :key="index"
            class="px-2 w-2/12 text-xs text-center"
          >
            {{ col.title }}
          </th>
        </tr>
      </thead>
    </table>

    <div class="table-wrapper">
      <div v-if="errored" class="empty ml-10 mt-4">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div id="content-area" v-else>
        <div
          style="width: 100%; text-align: center"
          class="spin-holder mt-4"
          v-if="loading"
        >
          <i class="spinner fa fa-spinner fa-spin"></i>
          <p>Loading Pupils</p>
        </div>

        <div v-else>
          <table
            v-if="componentLoaded"
            class="table-wrapper mx-auto bg-white w-95p rounded shadow-sm"
          >
            <tbody>
              <tr v-for="pupil in filteredList" :key="pupil.identifier">
                <td class="w-4/12">{{ pupil.name }}</td>
                <td
                  class="w-2/12 text-xs text-center cursor-pointer hover:opacity-50 "
                  v-for="outcome in swimOutcomes"
                  :key="outcome.identifier"
                  @click="setOutcome(pupil, outcome)"
                >
                  <i
                    class="icon list"
                    v-bind:class="hasOutcome(pupil, outcome)"
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <p class="m-5 mx-12" v-else>
            We could not find any data for your given selection.
          </p>
        </div>

        <div class="w-95p mx-auto">
          <Pagination
            v-if="componentLoaded"
            :total-pages="totalPages"
            :total="totalResultsCount"
            :current-page="currentPage"
            :has-more-pages="hasMorePages"
            :pageSize="pageSize"
            @pagechanged="showMore"
            class="mt-4 mb-12"
          >
          </Pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { PupilService } from "@/services/pupil.service";
import { SwimmingService } from "@/services/swimming.service";
import { calendarYearList } from "@/assets/js/utils";

const ClassDropdown = () => import("@/components/pupil/ClassDropdown.vue");

const Pagination = () => import("@/components/helpers/Pagination.vue");
const PupilFilterModal = () => import("@/components/helpers/PupilFilter.vue");
const FilterBar = () => import("@/components/FilterBarNavigation.vue");

export default {
  name: "pupils",
  mixins: [],
  props: {
    msg: { type: String, required: false },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    hasMorePages() {
      return this.pagination.has_more === true;
    },
    totalResultsCount() {
      return this.pagination.total_count;
    },
    totalPages() {
      return this.pagination.total_pages;
    },
    componentLoaded() {
      return this.filteredList.length > 0;
    },
    showMessage() {
      return this.msg != undefined && this.msg.length > 0;
    },
    searchValue() {
      return this.search;
    },
    filteredList() {
      let vm = this;
      let count = this.swimGrades.length
      return _.orderBy(vm.pupils, [vm.sortBy, "name"], [vm.sortDirection]);
    },
    filterHasValues() {
      var value = this.filterValue;
      if (
        value.group != undefined ||
        value.years != undefined ||
        value.types != undefined ||
        (value.tags != undefined && value.length > 2)
      ) {
        return true;
      }
    },
    classSelectedValue() {
      return this.filterValue.group;
    },
  },
  data() {
    return {
      filterIsVisible: false,
      pupils: [],
      swimOutcomes: [],
      swimGrades: [],

      loading: false,
      errored: false,

      page: 1,
      pageSize: 48,
      currentPage: 1,
      pagination: {},

      search: "",
      filterValue: {},

      sortBy: "yeargroup",
      sortDirection: "asc",
    };
  },
  created() {
    const awardId = 1; // hardcoded value
    SwimmingService.getOutcomesByAward(awardId)
      .then((response) => (this.swimOutcomes = response.data.data))
      .catch((err) => {
        this.errored = true;
      })
      .finally(() => {
        this.fetchGrades(awardId);
      });
  },
  mounted() {
    this.searchPupils();
  },
  methods: {
    fetchGrades(awardId) {
      SwimmingService.getGradesByAward(awardId)
        .then((response) => (this.swimGrades = response.data.data))
        .catch((err) => {
          this.errored = true;
        });
    },
    searchPupils() {
      this.loading = true;

      PupilService.getWithFilter(this.filterValue, this.page, this.pageSize)
        .then((response) => this.buildPupils(response.data))
        .catch((err) => {
          console.log("pupils " + err);
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildPupils(data) {
      this.pupils = data.data;
      this.pagination = data.meta.pagination;
    },
    checkPupilOutcome(pupil, outcome) {
      let found = this.swimGrades.find((grade) => {
        return (
          grade.register_identifier === pupil.identifier &&
          grade.question_identifier === outcome.identifier
        );
      });
      return found;
    },
    hasOutcome(pupil, outcome) {
      if (this.checkPupilOutcome(pupil, outcome) === undefined) {
        return "fas fa-times-circle text-gray-300";
      } else {
        return "fas fa-check-circle text-green-400";
      }
    },
    setOutcome(pupil, outcome) {
      let vm = this;

      let result = this.checkPupilOutcome(pupil, outcome);
      if (result === undefined) {
        let calendarYear = calendarYearList().find((y) => y.current === true);

        let payload = {
          register_identifier: pupil.identifier,
          calendar_schoolyear: calendarYear.value,
          type: "swimming",
          question_identifier: outcome.identifier,
          answer: "true",
        };

        SwimmingService.addGrade(payload)
          .then((response) => {
            payload.identifier = response.data.identifier;

            vm.swimGrades.push(payload);
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        SwimmingService.deleteGrade(result)
          .then((response) => {
            vm.swimGrades = vm.swimGrades.filter(
              (r) => r.identifier !== result.identifier
            );
          })
          .catch((err) => {
            console.log("GRADE DELETE error " + err);
          });
      }
    },
    showFilter() {
      this.filterIsVisible = !this.filterIsVisible;
    },
    updateFilteredClass(value) {
      if (value != undefined) {
        this.filterValue.group = value;
      } else {
        this.filterValue.group = {};
      }

      this.showMore({ page: 1, size: this.pageSize });
    },
    applyFilter(filters) {
      this.filterValue = filters;
      this.updateFilteredClass(filters.group);

      this.showFilter();
      this.showMore({ page: 1, size: this.pageSize });
    },
    setSearchValue(value, time = 300) {
      this.search = value;
      this.filterValue.title = this.search;

      let vm = this;
      if (vm.search.length === 0 || vm.search.length >= 1) {
        clearTimeout(vm.timeout);
        vm.timeout = setTimeout(() => {
          vm.showMore({ page: 1, size: this.pageSize });
        }, time);
      }
    },
    showMore({ page, size }) {
      this.page = page;
      this.currentPage = page;
      this.pageSize = size;
      this.searchPupils();
      window.scrollTo(0, 0);
    },
    sort(s) {
      if (s === this.sortBy) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      }
      this.sortBy = s;
    },
  },
  components: {
    PupilFilterModal,
    FilterBar,
    Pagination,
    ClassDropdown,
  },
};
</script>

<style>
</style>
